import React from 'react';
import { FormCard } from '@features/onboarding/components/shared/form-card/FormCard';
import {
  getCategoryIcon,
  getCategoryText,
} from '@features/onboarding/components/4-dental-and-vision-form/content/content';

import { isMedicalCategory, useSelectionStore } from '../store/selection';
import { categoryCopy } from '../constants/copy';
import type { PlanDetailsMap } from '../api/planDetails';
import { W2_SELECT_TID } from '../constants/testids';
import { PlanCard } from './PlanCard';
import { HealthStep } from './HealthStep';
import { W2SelectionStepLayout } from './W2SelectionStepLayout';

const TID = W2_SELECT_TID.plans;

interface PlanScreenProps {
  category: string;
  /** 1-based bar position (STEP n / m); drives the left-panel counter. */
  step: number;
  total: number;
  isFirst: boolean;
  isLast: boolean;
  saving: boolean;
  saveError: string | null;
  onBack: () => void;
  onNext: () => void;
  /** Joined full plan records (WP6), keyed by planId. Optional / graceful. */
  planDetails?: PlanDetailsMap;
}

/**
 * One per-plan screen for a single benefit category (Medical / Dental / Vision /
 * Accident / Critical Illness / Hospital Indemnity), hosted in the two-panel
 * shell. The verbatim category headline/body sit in the left maroon panel; the
 * plan list (single-choice `HealthStep` for Medical, per-plan `PlanCard`s for
 * ancillaries) fills the middle card. "Next" is gated until EVERY line in the
 * category carries a complete decision — never absence-as-answer.
 */
export const PlanScreen: React.FC<PlanScreenProps> = ({
  category,
  step,
  total,
  isFirst,
  isLast,
  saving,
  saveError,
  onBack,
  onNext,
  planDetails,
}) => {
  // Select the stable `offerings` reference (a filtering selector would return a
  // fresh array each render → an infinite useSyncExternalStore loop). Filter to
  // the active category in render instead.
  const allOfferings = useSelectionStore((s) => s.context?.offerings);
  const offerings = (allOfferings ?? []).filter((o) => o.category === category);
  const medical = isMedicalCategory(category);
  // Subscribe to `lines` + the medical fields so completeness (and the Next gate)
  // re-evaluates on every decision change across both the per-plan and the
  // single-choice Health paths.
  const lines = useSelectionStore((s) => s.lines);
  const medicalChoice = useSelectionStore((s) => s.medicalChoice);
  const medicalWaived = useSelectionStore((s) => s.medicalWaived);
  const medicalCertified = useSelectionStore((s) => s.medicalCertified);
  const isCategoryComplete = useSelectionStore((s) => s.isCategoryComplete);
  const complete = isCategoryComplete(category);
  void lines;
  void medicalChoice;
  void medicalWaived;
  void medicalCertified;

  const copy = categoryCopy(category);
  // The B2C category section header (icon + "A plan for potential critical
  // health moments" + subtitle) — the pure, store-free `FormCard` over the pure
  // `content.ts` strings, approved for W2 screens (captain decision D2). Only
  // the ancillary categories carry a `section` entry; Medical has none, and the
  // Health screen keeps its own headings.
  const section = getCategoryText(category).section;
  const CategoryIcon = getCategoryIcon(category);

  return (
    <W2SelectionStepLayout
      step={step}
      total={total}
      title={copy.headline}
      titleTestId={TID.category}
      body={<p>{copy.body}</p>}
      rootTestId={TID.root}
      onPrev={onBack}
      prevLabel={isFirst ? 'Back' : 'Previous'}
      prevTestId={TID.back}
      onNext={onNext}
      nextLabel={isLast ? 'Review my quote' : 'Next'}
      nextTestId={TID.next}
      nextDisabled={!complete || saving}
      nextLoading={saving}
    >
      {medical ? (
        <HealthStep details={planDetails} />
      ) : section ? (
        <FormCard
          title={section.title}
          description={section.description}
          iconComponent={CategoryIcon}
          classNameHeader="pb-6 border-b border-[#ebe9e1] mt-0"
        >
          <div className="mt-6 flex flex-col gap-4">
            {offerings.map((offering) => (
              <PlanCard
                key={offering.planId}
                offering={offering}
                detail={planDetails?.[offering.planId]}
              />
            ))}
          </div>
        </FormCard>
      ) : (
        <div className="flex flex-col gap-4">
          {offerings.map((offering) => (
            <PlanCard
              key={offering.planId}
              offering={offering}
              detail={planDetails?.[offering.planId]}
            />
          ))}
        </div>
      )}

      {!complete && (
        <p
          data-testid={TID.incomplete}
          className="mt-4 text-[14px] text-[#8a5a00]"
        >
          {medical
            ? 'Choose a health plan, or waive health coverage, to continue.'
            : 'Make a choice (elect or waive) for every plan above to continue.'}
        </p>
      )}

      {saveError && (
        <p
          data-testid={TID.saveError}
          role="alert"
          className="mt-4 text-[14px] text-[#c0392b]"
        >
          {saveError}
        </p>
      )}
    </W2SelectionStepLayout>
  );
};
